/**
 * revia-backoffice-shell — the staff frame, mirroring Revent's admin shell
 * and nav rail, which its views package does not export: the glass rail
 * headed by the mark that opens <app-settings>, the destinations, and the
 * organization plate as the last row (below `md`, the puck's sheet led by the
 * mark and the plate), beside the routed page over the starfield. The
 * backoffice serves Revia's organization alone, so the plate holds one
 * organization and its chooser is where staff sign out.
 */
import { area, lazy, tooltip } from "@mhmo91/schmancy";
import { art, fill, gravity, overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import "@momo/views/revia-logo";
import { AppSettings, session, signAction$, signResult$ } from "@momo/views";
import { REVIA_ORG_ID } from "@revia/types";
import { html } from "lit";
import { state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import {
	exhaustMap,
	filter,
	fromEvent,
	map,
	merge,
	Observable,
	of,
	Subject,
	take,
	takeUntil,
	tap,
} from "rxjs";
import { LocalizeMixin } from "../i18n/localize.ts";

// `value` is the route's `when` and readable path; `tag` the page it mounts,
// which is what the area reports.
const NAV_ITEMS = [
	{
		value: "requests",
		tag: "revia-backoffice-requests",
		labelKey: "backoffice.nav.requests",
		railKey: "backoffice.nav.requests",
		icon: "inbox",
	},
	{
		value: "licences",
		tag: "revia-backoffice-licences",
		labelKey: "backoffice.nav.licences",
		railKey: "backoffice.nav.licences",
		icon: "license",
	},
	{
		value: "visits",
		tag: "revia-backoffice-visits",
		labelKey: "backoffice.nav.visits",
		railKey: "backoffice.nav.visits",
		icon: "monitoring",
	},
] as const;

@schmancyElement("revia-backoffice-shell")
export class ReviaBackofficeShell extends LocalizeMixin(SchmancyElement) {
	@state() private active: string = "licences";
	@state() private signingOut = false;
	@state() private signOutFailed = false;

	private readonly openSettings$ = new Subject<MouseEvent | KeyboardEvent>();
	private readonly phone = window.matchMedia("(max-width: 767.98px)");

	override connectedCallback(): void {
		super.connectedCallback();
		merge(
			area.on("backoffice").pipe(
				map((route) => NAV_ITEMS.find((item) => item.tag === route.component || item.value === route.component)),
				filter((item) => item !== undefined),
				tap((item) => (this.active = item.value)),
			),

			fromEvent<CustomEvent<string>>(this, "navigate").pipe(
				map((e) => NAV_ITEMS.find((item) => item.value === e.detail)),
				filter((item) => item !== undefined),
				tap((item) =>
					area.push({
						area: "backoffice",
						component: item.value,
						path: `/${item.value}`,
						historyStrategy: "replace",
					}),
				),
			),

			// show() anchors a panel's corner on the gesture's point; the offset is
			// that point's distance to the control's right edge plus the overlay's
			// own 8px gap, so a panel placed right-start opens clear of it. The
			// phone sheet has no room beside the control, so there show() lays it out.
			this.openSettings$.pipe(
				exhaustMap((e) => {
					const control = e.currentTarget instanceof HTMLElement ? e.currentTarget : null;
					const box = control?.getBoundingClientRect();
					return show(
						AppSettings,
						this.phone.matches || box === undefined
							? { ariaLabel: this.t("backoffice.nav.preferences") }
							: {
									ariaLabel: this.t("backoffice.nav.preferences"),
									preferredPlacement: "right-start",
									offset:
										box.right -
										(e instanceof MouseEvent && e.detail > 0 ? e.clientX : box.left + box.width / 2) +
										8,
									grow: false,
								},
					);
				}),
			),

			// The plate's chooser confirms first and drops a press while this runs.
			// Subscribed to the answer before dispatching, so a synchronous one is heard.
			fromEvent(this, "sign-out").pipe(
				exhaustMap(
					() =>
						new Observable<boolean>((subscriber) => {
							this.signingOut = true;
							this.signOutFailed = false;
							const requestId = crypto.randomUUID();
							const sub = signResult$
								.pipe(
									filter((r) => r.requestId === requestId),
									take(1),
								)
								.subscribe((r) => {
									subscriber.next(r.ok);
									subscriber.complete();
								});
							signAction$.next({ kind: "signOut", requestId });
							return sub;
						}),
				),
				tap((ok) => {
					this.signingOut = false;
					this.signOutFailed = !ok;
				}),
			),

			// A refusal answers the press that asked; a reopened chooser starts clean.
			fromEvent(this, "chooser-close").pipe(tap(() => (this.signOutFailed = false))),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	override render() {
		const brandPreferences = this.t("backoffice.nav.brandPreferences", { brand: this.t("backoffice.brand") });
		const organizations = [{ id: REVIA_ORG_ID, name: this.t("backoffice.brand") }];
		return html`
			<div
				${fill()}
				${art({ name: "starfield", color: "var(--schmancy-sys-color-primary-default)", intensity: 0.4, density: 0.6 })}
				class="grid min-h-0 min-w-0 grid-cols-[auto_1fr]"
			>
				<schmancy-surface type="containerLowest" opacity="glass" class="flex h-full flex-col">
					<div
						role="button"
						tabindex="0"
						data-mark
						${tooltip(this.t("backoffice.nav.preferences"), { position: "right" })}
						aria-label=${brandPreferences}
						aria-haspopup="dialog"
						class="hidden w-20 shrink-0 cursor-pointer place-items-center self-center rounded-xl px-3 pb-3 pt-5 transition-opacity hover:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-default md:grid"
						@click=${(e: MouseEvent) => this.openSettings$.next(e)}
						@keydown=${(e: KeyboardEvent) => {
							if (e.key !== "Enter" && e.key !== " ") return;
							e.preventDefault();
							this.openSettings$.next(e);
						}}
					>
						<revia-logo animated compact unlinked aria-hidden="true" class="block h-12 w-12 shrink-0"></revia-logo>
					</div>
					<schmancy-navigation-rail
						class="min-h-0 flex-1 py-2"
						alignment="top"
						label-visibility="all"
						label-lines="2"
						.activeValue=${this.active}
					>
						<schmancy-button
							slot="sheet-header"
							variant="plain"
							.ariaLabel=${brandPreferences}
							aria-haspopup="dialog"
							class="flex min-h-11 items-center rounded-xl px-2 transition-opacity hover:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-default"
							@click=${(e: MouseEvent) => this.openSettings$.next(e)}
						>
							<revia-logo animated compact unlinked aria-hidden="true" class="block h-11 w-11 shrink-0"></revia-logo>
						</schmancy-button>
						<organization-plate
							slot="sheet-header"
							.organizations=${organizations}
							.organizationId=${REVIA_ORG_ID}
							.email=${session.value.email ?? ""}
							.signingOut=${this.signingOut}
							.signOutFailed=${this.signOutFailed}
						></organization-plate>
						${repeat(
							NAV_ITEMS,
							(item) => item.value,
							(item, i) => html`
								<schmancy-navigation-rail-item
									${gravity({ stagger: 60 * i, distance: 20, mass: 0.9 })}
									value=${item.value}
									label=${this.t(item.railKey)}
									icon=${item.icon}
									aria-label=${this.t(item.labelKey)}
									class="[&[active]::part(icon-text)]:text-[var(--color-primary-on-container)] [&[active]::part(label)]:text-[var(--color-primary-on-container)]"
								></schmancy-navigation-rail-item>
							`,
						)}
						<organization-plate
							data-plate
							class="mt-4 self-center"
							.organizations=${organizations}
							.organizationId=${REVIA_ORG_ID}
							.email=${session.value.email ?? ""}
							.signingOut=${this.signingOut}
							.signOutFailed=${this.signOutFailed}
						></organization-plate>
					</schmancy-navigation-rail>
				</schmancy-surface>

				<div ${overflowWithin()} class="col-start-2 min-h-0 min-w-0">
					<schmancy-area name="backoffice" .default=${"home"}>
						<!-- "/" names no page: the default lands the readable address in
						     its place, so nothing mounts twice and no history entry is added. -->
						<schmancy-route
							when="home"
							.component=${"revia-backoffice-licences"}
							.guard=${of(false)}
							@redirect=${() =>
								area.push({
									area: "backoffice",
									component: "licences",
									path: "/licences",
									historyStrategy: "replace",
								})}
						></schmancy-route>
						<schmancy-route
							when="requests"
							.component=${lazy(() => import("./revia-backoffice-requests.view.ts"))}
						></schmancy-route>
						<schmancy-route
							when="licences"
							.component=${lazy(() => import("./revia-backoffice-licences.view.ts"))}
						></schmancy-route>
						<schmancy-route
							when="visits"
							.component=${lazy(() => import("./revia-backoffice-visits.view.ts"))}
						></schmancy-route>
					</schmancy-area>
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"revia-backoffice-shell": ReviaBackofficeShell;
	}
}
